import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Plus, Percent, Trash2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format, addDays } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PromoCodes = () => {
  const { selectedProperty } = useOutletContext();
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'PERCENTAGE',
    discount_value: 0,
    min_nights: 1,
    valid_from: format(new Date(), 'yyyy-MM-dd'),
    valid_to: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    max_uses: null,
    enabled: true
  });

  useEffect(() => {
    if (selectedProperty) {
      fetchPromoCodes();
    }
  }, [selectedProperty]);

  const fetchPromoCodes = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/promo-codes/property/${selectedProperty.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromoCodes(response.data);
    } catch (error) {
      console.error('Error fetching promo codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePromoCode = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/promo-codes/property/${selectedProperty.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Promo code created successfully');
      setDialogOpen(false);
      setFormData({
        code: '',
        discount_type: 'PERCENTAGE',
        discount_value: 0,
        min_nights: 1,
        valid_from: format(new Date(), 'yyyy-MM-dd'),
        valid_to: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
        max_uses: null,
        enabled: true
      });
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promo code');
    }
  };

  const handleToggle = async (promoId, currentEnabled) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(
        `${API}/promo-codes/${promoId}?enabled=${!currentEnabled}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (promoId) => {
    if (!confirm('Delete this promo code?')) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/promo-codes/${promoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Promo code deleted');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">Please select a property</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="promo-codes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Promo Codes</h1>
          <p className="text-slate-600 mt-1">Create discount codes to boost bookings</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-promo-button">
              <Plus size={18} />
              New Promo Code
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Promo Code</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreatePromoCode} className="space-y-4" data-testid="promo-form">
              <div>
                <Label>Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  placeholder="SUMMER2025"
                  required
                  data-testid="promo-code-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Discount Type *</Label>
                  <Select value={formData.discount_type} onValueChange={(val) => setFormData({...formData, discount_type: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PERCENTAGE">Percentage</SelectItem>
                      <SelectItem value="FIXED">Fixed Amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Discount Value *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({...formData, discount_value: parseFloat(e.target.value)})}
                    required
                    data-testid="discount-value-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Valid From *</Label>
                  <Input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({...formData, valid_from: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Valid To *</Label>
                  <Input
                    type="date"
                    value={formData.valid_to}
                    onChange={(e) => setFormData({...formData, valid_to: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Min Nights</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.min_nights}
                    onChange={(e) => setFormData({...formData, min_nights: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Max Uses (optional)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.max_uses || ''}
                    onChange={(e) => setFormData({...formData, max_uses: e.target.value ? parseInt(e.target.value) : null})}
                    placeholder="Unlimited"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full" data-testid="submit-promo-button">
                Create Promo Code
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {promoCodes.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Percent size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">No promo codes yet. Create discount codes to attract guests.</p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus size={18} className="mr-2" />
              Create First Promo Code
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="promo-codes-list">
          {promoCodes.map((promo) => (
            <Card key={promo.id} className="hover:shadow-lg transition-shadow" data-testid={`promo-${promo.id}`}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Percent size={20} className="text-cyan-600" />
                    <span className="font-mono">{promo.code}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(promo.code)}
                    >
                      <Copy size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(promo.id)}
                    >
                      <Trash2 size={16} className="text-red-500" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-3xl font-bold text-cyan-600">
                    {promo.discount_type === 'PERCENTAGE' ? `${promo.discount_value}%` : `$${promo.discount_value}`}
                  </p>
                  <p className="text-sm text-slate-500">
                    {promo.discount_type === 'PERCENTAGE' ? 'Percentage Off' : 'Fixed Discount'}
                  </p>
                </div>

                <div className="space-y-1 text-sm">
                  <p className="text-slate-600">
                    Valid: {format(new Date(promo.valid_from), 'MMM dd')} - {format(new Date(promo.valid_to), 'MMM dd, yyyy')}
                  </p>
                  {promo.min_nights > 1 && (
                    <p className="text-slate-600">Min {promo.min_nights} nights</p>
                  )}
                  {promo.max_uses && (
                    <p className="text-slate-600">
                      Uses: {promo.current_uses}/{promo.max_uses}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between pt-3 border-t">
                  <Label className="text-sm">Active</Label>
                  <Switch
                    checked={promo.enabled}
                    onCheckedChange={() => handleToggle(promo.id, promo.enabled)}
                    data-testid={`toggle-${promo.id}`}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PromoCodes;
